import { type MoneyMapNewFruitfulAccount } from '@fruitful/types';
import React, { type ReactNode } from 'react';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallEmphasized,
    CreditCardIcon,
    FruitfulCustomIcon,
    Icon,
    Info,
    isNonMobileWeb,
    LabelRegular,
    XStack,
    YStack,
} from 'smoothie';

import { GoWorkflowStepType } from '../../../roadmap/types/GoWorkflow';
import { type CardColor } from '../../types/Card';
import { FruitfulCardsGraphic } from '../cards/FruitfulCard';

const CashAccountRow = ({
    account,
}: {
    account: MoneyMapNewFruitfulAccount;
}) => {
    return (
        <XStack gap={12} alignItems="center">
            <FruitfulCustomIcon
                size="sm"
                icon={account.createParameters?.icon}
                color={account.createParameters?.color}
            />
            <BodyMediumRegular>
                {account.createParameters?.name}
            </BodyMediumRegular>
        </XStack>
    );
};

interface CardAccountProps {
    name?: string;
    hasPhysicalCard: boolean;
    hasVirtualCard: boolean;
    virtualCardColor: CardColor;
}

export const CardAccountRow = (props: CardAccountProps) => {
    const { hasPhysicalCard, hasVirtualCard, name, virtualCardColor } = props;

    return (
        <XStack gap={12} alignItems="center">
            <FruitfulCardsGraphic
                hasPhysicalCard={hasPhysicalCard}
                hasVirtualCard={hasVirtualCard}
                virtualCardColor={virtualCardColor}
            />
            <YStack>
                <BodyMediumRegular>{name}</BodyMediumRegular>
                <XStack gap={12}>
                    {hasVirtualCard && (
                        <XStack gap={4} alignItems="center">
                            <Icon
                                icon={CreditCardIcon}
                                size={13}
                                color="$gray900"
                            />
                            <LabelRegular color="$gray900">
                                Virtual Card
                            </LabelRegular>
                        </XStack>
                    )}
                    {hasPhysicalCard && (
                        <XStack gap={4} alignItems="center">
                            <Icon
                                icon={CreditCardIcon}
                                size={13}
                                color="$gray900"
                            />
                            <LabelRegular color="$gray900">
                                Physical Card
                            </LabelRegular>
                        </XStack>
                    )}
                </XStack>
            </YStack>
        </XStack>
    );
};

const AccountList = ({
    currentStepType,
    accounts,
    header,
}: {
    currentStepType: GoWorkflowStepType;
    accounts: MoneyMapNewFruitfulAccount[];
    header?: string;
}) => {
    if (accounts.length === 0) {
        return null;
    }

    return (
        <>
            {isNonMobileWeb ? (
                <BodyLargeRegular>{header}</BodyLargeRegular>
            ) : (
                <BodySmallEmphasized>{header}</BodySmallEmphasized>
            )}
            <YStack gap={16} paddingBottom={8}>
                {accounts.map((acct) => {
                    return currentStepType ===
                        GoWorkflowStepType.CASH_ACCOUNTS ? (
                        <CashAccountRow
                            account={acct}
                            key={acct.createParameters?.name}
                        />
                    ) : currentStepType === GoWorkflowStepType.CARD_ACCOUNTS ? (
                        <CardAccountRow
                            hasPhysicalCard={
                                !!acct?.createParameters?.physicalCard
                            }
                            hasVirtualCard={
                                !!acct?.createParameters?.virtualCard
                            }
                            name={acct?.createParameters?.name}
                            virtualCardColor={
                                (acct?.createParameters?.color as CardColor) ??
                                undefined
                            }
                            key={acct.createParameters?.name}
                        />
                    ) : null;
                })}
            </YStack>
        </>
    );
};

const getJointAccountsToAcceptHeader = (
    step: GoWorkflowStepType,
    accounts: MoneyMapNewFruitfulAccount[],
) => {
    const singleAccount = accounts.length === 1;
    const singleCard = singleAccount
        ? accounts?.[0]?.createParameters?.physicalCard != null // all accounts have virtual cards, so if this one account has a physical card too, then we have more than one card
        : false;

    if (step === GoWorkflowStepType.CASH_ACCOUNTS) {
        return singleAccount
            ? `Accept this joint Fruitful Cash account invitation`
            : `Accept these joint Fruitful Cash account invitations`;
    }
    if (step === GoWorkflowStepType.CARD_ACCOUNTS) {
        return singleCard
            ? `Open this Fruitful Card for your joint ${singleAccount ? 'account' : 'accounts'}`
            : `Open these Fruitful Cards for your joint ${singleAccount ? 'account' : 'accounts'}`;
    }
    return '';
};

const getJointAccountsToOpenHeader = (
    step: GoWorkflowStepType,
    accounts: MoneyMapNewFruitfulAccount[],
) => {
    const singleAccount = accounts.length === 1;
    const singleCard = singleAccount
        ? accounts?.[0]?.createParameters?.physicalCard != null // all accounts have virtual cards, so if this one account has a physical card too, then we have more than one card
        : false;

    if (step === GoWorkflowStepType.CASH_ACCOUNTS) {
        return singleAccount
            ? `Open this joint Fruitful Cash account`
            : `Open these joint Fruitful Cash accounts`;
    }

    if (step === GoWorkflowStepType.CARD_ACCOUNTS) {
        return singleCard
            ? `Open this Fruitful Card for your joint ${singleAccount ? 'account' : 'accounts'}`
            : `Open these Fruitful Cards for your joint ${singleAccount ? 'account' : 'accounts'}`;
    }

    return '';
};

const getSoloAccountsToOpenHeader = (
    step: GoWorkflowStepType,
    accounts: MoneyMapNewFruitfulAccount[],
    hasJoint: boolean,
) => {
    const singleAccount = accounts.length === 1;
    const singleCard = singleAccount
        ? accounts?.[0]?.createParameters?.physicalCard != null // all accounts have virtual cards, so if this one account has a physical card too, then we have more than one card
        : false;

    if (step === GoWorkflowStepType.CASH_ACCOUNTS) {
        if (hasJoint) {
            return singleAccount
                ? `Open this individual Fruitful Cash account`
                : `Open these individual Fruitful Cash accounts`;
        } else {
            return singleAccount
                ? `Open this Fruitful Cash account`
                : `Open these Fruitful Cash accounts`;
        }
    }

    if (step === GoWorkflowStepType.CARD_ACCOUNTS) {
        if (hasJoint) {
            return singleCard
                ? `Open this Fruitful Card for your individual ${singleAccount ? 'account' : 'accounts'}`
                : `Open these Fruitful Cards for your individual ${singleAccount ? 'account' : 'accounts'}`;
        } else {
            return singleCard
                ? `Open this Fruitful Card for your ${singleAccount ? 'account' : 'accounts'}`
                : `Open these Fruitful Cards for your ${singleAccount ? 'account' : 'accounts'}`;
        }
    }

    return '';
};

export const AccountsSection = ({
    currentStepType,
    soloAccountsToOpen,
    jointAccountsToOpen,
    jointAccountsToAccept,
    learnMoreText,
    onLearnMorePress,
}: {
    currentStepType: GoWorkflowStepType;
    soloAccountsToOpen: MoneyMapNewFruitfulAccount[];
    jointAccountsToOpen: MoneyMapNewFruitfulAccount[];
    jointAccountsToAccept: MoneyMapNewFruitfulAccount[];
    learnMoreText: ReactNode;
    onLearnMorePress: () => void;
}) => {
    const hasJoint =
        jointAccountsToAccept.length > 0 || jointAccountsToOpen.length > 0;

    const jointAccountsToAcceptHeader = getJointAccountsToAcceptHeader(
        currentStepType,
        jointAccountsToAccept,
    );

    const jointAccountsToOpenHeader = getJointAccountsToOpenHeader(
        currentStepType,
        jointAccountsToOpen,
    );

    const soloAccountsToOpenHeader = getSoloAccountsToOpenHeader(
        currentStepType,
        soloAccountsToOpen,
        hasJoint,
    );

    return (
        <YStack width="100%" gap={24}>
            <XStack
                gap={12}
                padding={16}
                borderRadius={24}
                backgroundColor="$green400"
                alignItems="center"
                cursor="pointer"
                onPress={onLearnMorePress}
            >
                <Icon icon={Info} size={24} />
                {learnMoreText}
            </XStack>
            <AccountList
                currentStepType={currentStepType}
                accounts={jointAccountsToAccept}
                header={jointAccountsToAcceptHeader}
            />
            <AccountList
                currentStepType={currentStepType}
                accounts={jointAccountsToOpen}
                header={jointAccountsToOpenHeader}
            />
            <AccountList
                currentStepType={currentStepType}
                accounts={soloAccountsToOpen}
                header={soloAccountsToOpenHeader}
            />
        </YStack>
    );
};
